import {
  fetchGuideStepCreate,
  fetchGuideStepDelete,
  fetchGuideStepUpdate,
  guideQueryOptions,
  guidesCountQueryOptions,
  useGuideUpdateMutation,
} from '@blesk-frontend/api';
import {Page} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {useEffect} from 'react';
import {toast} from 'sonner';

import {GuideForm, GuideFormData, GuideSkeleton} from '@/features/guides';
import {sendEvent} from '@/libs/analytics';
import {Route} from '@/routes/_app/guides/$guideId.edit';

export const GuideEditPage = () => {
  const navigate = Route.useNavigate();
  const {data: guide, isLoading, refetch} = useQuery(guideQueryOptions(Route.useParams().guideId));
  const {mutateAsync: guideMutateAsync} = useGuideUpdateMutation();
  const {data: count} = useQuery(guidesCountQueryOptions());

  const searchParams = Route.useSearch();

  useEffect(() => {
    window.scrollTo(0, 0);

    if (searchParams.new) {
      sendEvent('guides/create');
    }
  }, [searchParams]);

  const onSubmit = async (data: GuideFormData) => {
    if (!guide) {
      toast.error('Произошла ошибка');
      return;
    }

    await guideMutateAsync({
      id: guide.id,
      name: data.name,
      description: data.description,
    });

    const toCreateSteps = data.steps.filter((step) => !guide.steps.some((s) => s.id === step.id));

    const toDeleteSteps = guide.steps.filter((step) => !data.steps.some((s) => s.id === step.id));

    const toUpdateSteps = data.steps.filter((step) => guide.steps.some((s) => s.id === step.id));

    await Promise.all([
      ...toCreateSteps.map((step) => fetchGuideStepCreate(guide.id, step)),
      ...toDeleteSteps.map((step) => fetchGuideStepDelete(guide.id, step.id)),
      ...toUpdateSteps.map((step) => fetchGuideStepUpdate(guide.id, step)),
    ]);

    const firstGuide = (count?.archived || 0) + (count?.unarchived || 0) === 1;

    await refetch();

    await navigate({
      to: '/guides/$guideId',
      params: {guideId: guide.id},
      search: {
        ...(searchParams.new && firstGuide && {export: true}),
        ...(searchParams.discountId && {discountId: searchParams.discountId}),
      },
    });

    toast.success('Изменения сохранены');
    window.scrollTo(0, 0);
  };

  if (isLoading || !guide) {
    return <GuideSkeleton />;
  }

  return (
    <Page>
      <GuideForm guide={guide} onSubmit={onSubmit} />
    </Page>
  );
};
